import ReactJson from "react-json-view";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import {
    fetchBenefitPlanTableScan, fetchHrpTableItemsByHccID, fetchBenefitPlanTableItemsByPk, fetchHrpTableItemsBySk, fetchHrpTableItemsBySparse, fetchHrpTableScan, fetchItemsByRetryOn, fetchNotificationsJobRegistryTableItemsByPk,
    fetchNotificationsJobRegistryTableScan,
    fetchJobs,
    fetchJobRegistryJobHasError
} from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";
import { queryClient } from "../../App";
import DatePicker from "react-datepicker";
import ToggleSwitch from "../shared/ToggleSwitch";
import { getTimeDiff } from "../../utils/commonUtils";
import Select from 'react-select';
import { exportToCsv } from "../../utils/downloadUtils";

const searchOptions = [
    { value: 'JobId', label: 'JobId', additional: { pkKey: 'pk', skKey: 'sk' } },
    { value: 'invertedIndex', label: 'invertedIndex', additional: { pkKey: 'sk', skKey: 'pk' } },
    { value: 'sparseIndex', label: 'sparseIndex', additional: { pkKey: 'sparse', skKey: 'pk' } }
]

const sparseOptions = {
    STARTED: 'STARTED',
    COMPLETED: 'COMPLETED',
    ERROR: "ERROR",
    PENDING: "PENDING",
    ACTION_REQURED: "ACTION_REQURED",
    RUNNING: 'RUNNING',
}

const Jobs = ({ subscriptionHccId, autoRefreshEnabled: parentAutoRefreshEnabled }) => {

    const now = new Date()

    const timezoneOffsetMs = now.getTimezoneOffset() * 60000;
    const localStartOfDayNow = new Date(now.getTime() - timezoneOffsetMs);
    localStartOfDayNow.setHours(0, 0, 0, 0);
    const [showMoreColumns, setShowMoreColumns] = useState(false);


    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [searchPkValue, setSearchPkValue] = useState();
    const [decouncedSearchPkValue, setDecouncedSearchPkValue] = useState();

    const [searchSkValue, setSearchSkValue] = useState();
    const [decouncedSearchSkValue, setDecouncedSearchSkValue] = useState();

    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(parentAutoRefreshEnabled)

    const [selectedDate, setSelectedDate] = useState(localStartOfDayNow);

    const [startDate, setStartDate] = useState(localStartOfDayNow);
    const [endDate, setEndDate] = useState(localStartOfDayNow);


    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadingError, setDownloadingError] = useState('')

    useEffect(() => {
        setAutoRefreshEnabled(parentAutoRefreshEnabled)

    }, [parentAutoRefreshEnabled])

    useEffect(() => {
        if (subscriptionHccId) {
            setSelectedSearchOption({ value: 'hccId', label: 'hccId' })
            setSearchPkValue(subscriptionHccId)
            setDecouncedSearchPkValue(subscriptionHccId)
        }

    }, [subscriptionHccId])

    const checkIfJobHasError = async (item) => {
        const result = await fetchJobRegistryJobHasError({ pk: item.pk, sk: item.sk })
        return { ...item, hasError: result.hasError, status: result.hasError && item.status === 'COMPLETED' ? 'ERROR' : item.status }
    }

    const { ref, inView } = useInView();

    const queryKey = ['job-registry-table', startDate, endDate, selectedSearchOption?.value, decouncedSearchPkValue, decouncedSearchSkValue];

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: async ({ pageParam }) => {
            let params = { lastEvaluatedKey: pageParam };

            if (startDate) {

                startDate.setHours(0, 0, 0, 0); // Force to local midnight
                params.startDate = startDate.toISOString()
            }

            if (endDate) {

                endDate.setHours(0, 0, 0, 0); //.setHours(23, 59, 59, 999);// Force to local midnight
                params.endDate = endDate.toISOString()
            }


            if (['invertedIndex', 'sparseIndex'].includes(selectedSearchOption?.value)) {
                params.indexName = selectedSearchOption?.value
            }

            if (decouncedSearchPkValue && selectedSearchOption?.value) {
                // params[selectedSearchOption?.value] = decouncedSearchValue;

                if (decouncedSearchSkValue) {
                    params.skKey = selectedSearchOption?.additional?.skKey
                    params.skValue = decouncedSearchSkValue
                }

                return fetchNotificationsJobRegistryTableItemsByPk({
                    ...params,
                    pkKey: selectedSearchOption?.additional?.pkKey,
                    pkValue: decouncedSearchPkValue
                })


            }

            const result = await fetchJobs(params)
            const errorCheckTasks = result.Items.map(item => checkIfJobHasError(item))
            const items = await Promise.all(errorCheckTasks)

            return { ...result, Items: items }
        },
        select: (data) => {
            return {
                pages: data.pages.map((page) => page.Items?.map(item => {
                    if (item.requestBody) {
                        try {
                            item.requestBodyParsed = JSON.parse(item.requestBody)
                        } catch (err) {
                            console.log(err)
                        }
                    }

                    if (item.startedAt && item.completedAt) {
                        const date1 = new Date(item.startedAt);
                        const date2 = new Date(item.completedAt);
                        item.duration = getTimeDiff({ date1, date2 })
                    }
                    return item
                })).flatMap((x) => x),
                pageParams: data.pageParams,
            };
            // return { ...data.hits, timestamp: Date.now() }
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        staleTime: 0,//,Infinity,
        enabled: true  //&& !!showChat
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchPkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const debouncedSkChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchSkValue(event.target.value?.trim());
        }, 500),
        []
    );

    // const resutls = data?.pages.flat()
    const items = data?.pages.flat();

    let columns = ['jobName', 'startedAt', 'completedAt', 'status', 'duration', 'count', 'tags', 'errorMessage'];

    if (showMoreColumns) {
        columns = [
            'pk', 'sk',
            ...columns,
            'note',
            'createdAt', 'updatedAt',

        ];
    }

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 1);

    const onChange = (dates) => {
        const [start, end] = dates;
        // setSelectedCategory(null); setSelectedSubCategory(null); setSelectedCallerType(null)
        setStartDate(start);
        setEndDate(end);
    };

    const loadAll = async () => {
        if (hasNextPage && !isFetchingNextPage) {
            const response = await fetchNextPage();
            if (response.hasNextPage) {
                await loadAll();
            }
        }
    }

    async function downloadHandler() {
        setIsDownloading(true)
        setDownloadingError(null)

        try {
            await loadAll()
            const fileNamePrefix = `Jobs_${new Date().toISOString()}`
            console.log({ items })
            exportToCsv({
                fileNamePrefix: fileNamePrefix,
                items, //state.items,
                headers: columns,
            });
        } catch (err) {
            console.log(err)
            setDownloadingError(err.message)

        } finally {
            setIsDownloading(false)
        }
    }

    const currenlyDisplayingCount = items?.length || 0
    return (
        <div className="container">
            {!subscriptionHccId &&
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                        <p className="title">Jobs ({currenlyDisplayingCount})</p>
                        {/* <p className="sub-title">Search hints:   </p> */}


                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem' }}>
                        {/* <p onClick={loadAll}>Load All</p> */}


                        <ToggleSwitch
                            label={`Auto Refresh`}
                            onToggle={(val) => setAutoRefreshEnabled(val)}
                            initialState={autoRefreshEnabled}
                        />
                    </div>

                </div>
            }

            {!subscriptionHccId && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem', minWidth: 200 }}>
                        <label style={{ fontSize: '.9rem', color: 'var(--darkGrey)' }} htmlFor="date">Filter by date range</label>

                        <DatePicker
                            className="date-picker"
                            selected={startDate}
                            onChange={onChange}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange // This prop enables date range selection
                            inline={false}
                            dateFormat={'yyyy-MM-dd'} // Renders the calendar inline (optional)
                            placeholderText="Select a date range"
                            maxDate={maxDate}
                        />
                        {/* <p style={{ paddingLeft: '.4rem' }}>Local: {selectedDate?.toLocaleString()}</p> */}


                    </div>
                    {/* <Select
                        name={'searchOptions'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        options={searchOptions}
                        value={selectedSearchOption}
                        onChange={(option) => {
                            setSelectedSearchOption(option);
                            if (option.value === 'isFromIndex') {
                                setSearchPkValue('HRP');
                                setDecouncedSearchPkValue('HRP')
                            } else {
                                setSearchPkValue('');
                                setDecouncedSearchPkValue('');
                                setSearchSkValue('');
                                setDecouncedSearchSkValue('')
                            }
                        }}
                    /> */}


                    {selectedSearchOption?.value && <> <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder={selectedSearchOption?.additional?.pkKey}
                        autoComplete="off"
                        onChange={(e) => {

                            setSearchPkValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchPkValue}
                    />
                        <input
                            style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                            type="text"
                            id={"sk"}
                            placeholder={selectedSearchOption?.additional?.skKey}
                            autoComplete="off"
                            onChange={(e) => {

                                setSearchSkValue(e.target.value);
                                debouncedSkChangeHandler(e);
                            }}
                            value={searchSkValue}
                        />
                    </>
                    }


                </div>
            </div>}


            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem", }}>
                {subscriptionHccId ? <p></p> : <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {

                        setSelectedSearchOption(null)
                        setSearchPkValue('');
                        setDecouncedSearchPkValue('');
                        setSearchSkValue('');
                        setDecouncedSearchSkValue('')
                        setSelectedDate(localStartOfDayNow)
                    }}
                >
                    Clear filters
                </p>}

                <div style={{ display: 'flex', gap: '1rem' }}>

                    {isDownloading ? <div className="loader" /> : <div >
                        <p className="clickable-link" style={{ color: 'var(--green)', padding: '0rem 1rem' }} onClick={downloadHandler}>{downloadingError ? 'Retry' : 'Download'}</p>
                        {downloadingError && <p className="danger" style={{ padding: '0rem 1rem' }}>{downloadingError}</p>}
                    </div>}
                    <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                        {showMoreColumns ? "Less Columns" : "More Columns"}
                    </p>

                </div>

            </div>


            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"job-registry"}
                        columns={columns}
                        items={items}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        autoRefreshEnabled={autoRefreshEnabled}
                        // onDetailHandler={onDetailHandler}
                        ref={ref}
                    />
                </>
            )}


        </div>
    )
}

export default Jobs